import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/types/api';
import type {
  SocietyEmergency,
  EmergencyDetailResponse,
  CreateEmergencyPayload,
  ResolveEmergencyPayload,
} from '@/types/emergency';

export const emergencyService = {
  /**
   * Raises a new Society-wide emergency alert.
   */
  raiseEmergency: async (payload: CreateEmergencyPayload): Promise<ApiResponse<SocietyEmergency>> => {
    const res = await apiClient.post<ApiResponse<SocietyEmergency>>('/emergencies', payload);
    return res.data;
  },

  /**
   * Retrieves active emergency for current Society (if any).
   */
  getActiveEmergency: async (): Promise<ApiResponse<SocietyEmergency | null>> => {
    const res = await apiClient.get<ApiResponse<SocietyEmergency | null>>('/emergencies/active');
    return res.data;
  },

  /**
   * Retrieves emergency details + acknowledgment summary.
   */
  getEmergencyById: async (id: string): Promise<ApiResponse<EmergencyDetailResponse>> => {
    const res = await apiClient.get<ApiResponse<EmergencyDetailResponse>>(`/emergencies/${id}`);
    return res.data;
  },

  /**
   * Lists historical emergencies with pagination.
   */
  listEmergencies: async (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<SocietyEmergency[]>> => {
    const res = await apiClient.get<ApiResponse<SocietyEmergency[]>>('/emergencies', { params });
    return res.data;
  },

  /**
   * Acknowledges an emergency ("I saw the alert / Stop Alert").
   */
  acknowledgeEmergency: async (id: string): Promise<ApiResponse<{ success: boolean; acknowledgedAt: string }>> => {
    const res = await apiClient.post<ApiResponse<{ success: boolean; acknowledgedAt: string }>>(
      `/emergencies/${id}/acknowledge`,
      {},
    );
    return res.data;
  },

  /**
   * Resolves an emergency. Restricted to Board members.
   */
  resolveEmergency: async (
    id: string,
    payload: ResolveEmergencyPayload,
  ): Promise<ApiResponse<SocietyEmergency>> => {
    const res = await apiClient.post<ApiResponse<SocietyEmergency>>(`/emergencies/${id}/resolve`, payload);
    return res.data;
  },
};
